import { designTokens, RiAlertFill } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import { FilterBar } from "@itandi/itandi-bb-ui/FilterBar";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import { formatDate } from "@itandi/tools/vanilla";
import { useCallback, useState } from "react";
import useSWRMutation from "swr/mutation";
import { DealClientTodoModal } from "~/components/common/deals/show/main/todos/DealClientTodoModal";
import { renderDropDownButton } from "~/components/common/parts/dropdown/DropDownButton";
import { useDealContext } from "~/context/DealContext";
import { dealTodosCacheKey, useDealTodos } from "~/hooks/deals/useDealTodos";
import { useEnabledStaffs } from "~/hooks/useStaffs";
import type { DealTodo } from "~/models/DealTodo";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy, update } from "~/utils/api/deals/DealTodo";
import { getTodayISO8601 } from "~/utils/DateUtil";

export function DealClientTodoTable() {
	const { notify } = useNotification();
	const deal = useDealContext();
	const { data: todos } = useDealTodos(deal.id);
	const staffs = useEnabledStaffs().data;
	const [showMode, setShowMode] = useState<"all" | "undone">("undone");
	const [editingTodo, setEditingTodo] = useState<DealTodo | null>(null);
	const [deletingTodo, setDeletingTodo] = useState<DealTodo | null>(null);

	const { trigger: triggerUpdateTodo } = useSWRMutation(
		dealTodosCacheKey(deal.id),
		async (_key, { arg }: { arg: DealTodo }) => {
			return await update(deal.id, arg.id, arg);
		}
	);
	const { trigger: triggerDeleteTodo, isMutating: isDeleting } = useSWRMutation(
		dealTodosCacheKey(deal.id),
		async (_key, { arg }: { arg: DealTodo }) => {
			return await destroy(deal.id, arg.id);
		}
	);

	const handleAcceptDelete = useCallback(async (): Promise<void> => {
		const response = await triggerDeleteTodo(deletingTodo!);
		if (isSuccess(response)) {
			setDeletingTodo(null);
			notify({ content: "TODOを削除しました。", variant: "success" });
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	}, [deletingTodo, notify, triggerDeleteTodo]);

	const onCheck = useCallback(
		(todo: DealTodo) => (): void => {
			const completed = !todo.completed;
			const newTodo =
				completed && todo.completedAt === null
					? {
							...todo,
							completed: true,
							completedAt: getTodayISO8601()
						}
					: { ...todo, completed };

			void triggerUpdateTodo(newTodo);
		},
		[triggerUpdateTodo]
	);

	if (todos === null || staffs === null) {
		return null;
	}

	return (
		<div>
			<Box mb={designTokens["spacing/s"]}>
				<FilterBar
					onChange={(value) => {
						setShowMode(value);
					}}
					options={[
						{ label: "未完了のみ表示", value: "undone" },
						{ label: "すべて表示", value: "all" }
					]}
					size="s"
					value={showMode}
				/>
			</Box>
			<Table>
				<Thead>
					<Tr>
						<Th width="3rem">完了</Th>
						<Th>タイトル</Th>
						<Th>担当者</Th>
						<Th>対応期限</Th>
						<Th>完了日</Th>
						<Th>操作</Th>
					</Tr>
				</Thead>
				<Tbody>
					{todos
						.map((todo) => {
							if (showMode === "undone" && todo.completed) {
								return null;
							}

							return (
								<Tr key={todo.id}>
									<Td>
										<InputCheckBox
											checked={todo.completed}
											onChange={onCheck(todo)}
										/>
									</Td>
									<Td>
										<TextLink
											onClick={() => {
												setEditingTodo(todo);
											}}
										>
											{todo.title}
										</TextLink>
									</Td>
									<Td>
										{todo.staff === null ? (
											<Flex
												alignItems="center"
												display="inline-flex"
												gap={designTokens["spacing/xxxs"]}
											>
												<Label color="warning" size="xs">
													設定なし
												</Label>
												<RiAlertFill
													color={designTokens["color/icon/warning"]}
													size={designTokens["size/icon/s"]}
												/>
											</Flex>
										) : (
											<Label size="xs">{todo.staff.name}</Label>
										)}
									</Td>
									<Td>
										<Label
											color={todo.dueDate === null ? "subtle" : undefined}
											size="xs"
										>
											{formatDate(todo.dueDate, "未登録")}
										</Label>
									</Td>
									<Td>
										<Label
											color={todo.completedAt === null ? "subtle" : undefined}
											size="xs"
										>
											{formatDate(todo.completedAt, "未完了")}
										</Label>
									</Td>
									<Td>
										<Dropdown button={renderDropDownButton("操作")}>
											<DropdownMenu
												danger
												onClick={() => {
													setDeletingTodo(todo);
												}}
											>
												TODOを削除
											</DropdownMenu>
										</Dropdown>
									</Td>
								</Tr>
							);
						})
						.filter((row) => row !== null)}
				</Tbody>
			</Table>
			{editingTodo !== null ? (
				<DealClientTodoModal
					deal={deal}
					handleClose={() => {
						setEditingTodo(null);
					}}
					staffs={staffs}
					todo={editingTodo}
				/>
			) : null}
			{deletingTodo !== null ? (
				<Modalv2
					isOpen
					onClose={() => {
						setDeletingTodo(null);
					}}
					size="s"
				>
					<ModalHeader title="TODOの削除" />
					<ModalBody>
						<Paragraph>TODOを削除します。</Paragraph>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Buttonv2
								disabled={isDeleting}
								onClick={() => {
									setDeletingTodo(null);
								}}
								variant="secondary"
							>
								キャンセル
							</Buttonv2>
						}
						rightArea={
							<Buttonv2
								color="danger"
								loading={isDeleting}
								onClick={handleAcceptDelete}
								variant="secondary"
							>
								削除
							</Buttonv2>
						}
					/>
				</Modalv2>
			) : null}
		</div>
	);
}
